import { Box, Flex, TabPanel, TabPanels, Tabs, VStack } from '@chakra-ui/react'
import { WithId } from '@chem-po/core'
import { useApp } from '@hb/react'
import { User } from '@hb/shared'
import { useCallback, useMemo, useRef } from 'react'
import { GynProfileEncounters } from '../../../../charting/Encounters/GynProfileEncounters'
import { useProfile } from '../../../../contexts/user/ProfileContext'
import { useResizeObserver } from '../../../../hooks/useResizeObserver'
import { FloatingContainer } from '../../../Containers'
import { GynCareViewProvider } from '../../../Gyn/context'
import { GynIntakeForm } from '../../../Gyn/GynIntakeForm'
import { Loading } from '../../../Loading'
import { GynLog } from '../Log/GynLog'
import { Resizable } from '../Log/Resizable'
import { GynDesktopTabs } from '../Tabs/GynDesktopTabs'

const GynProfileViewContent = ({
  width,
  user,
  height,
}: {
  width: number
  user: WithId<User> | null
  height: number
}) => {
  const { appName } = useApp()
  const mobileLayout = useMemo(() => width < 500, [width])
  const { goTo: onTabSelect, subTab, subTabs } = useProfile()

  const tabIndex = useMemo(() => {
    if (!subTab) return 0
    const idx = subTabs.findIndex(t => t.path === subTab.path)
    return idx >= 0 ? idx : 0
  }, [subTabs, subTab])

  const onTabChange = useCallback(
    (tabIndex: number) => {
      const selectedTab = subTabs[tabIndex]
      if (selectedTab) {
        onTabSelect({
          entity: { type: 'patient' },
          tab: `gyn/${selectedTab.path}`,
        })
      }
    },
    [onTabSelect, subTabs],
  )
  if (!user) {
    return (
      <VStack spacing={2} px={4} py={4} width="100%" bg="gray.50">
        <FloatingContainer px={0} py={0} bg="white">
          <Loading text="Loading patient Gyn information..." />
        </FloatingContainer>
      </VStack>
    )
  }

  return (
    <GynCareViewProvider user={user} admin canChangePractice={appName === 'app'}>
      <Flex w="100%" h={`${height}px`} overflowY="auto" bg="#f5f5f5">
        <GynDesktopTabs mobileLayout={mobileLayout} />
        <Tabs
          height="100%"
          display="flex"
          flex={1}
          minW={0}
          flexFlow="column"
          onChange={onTabChange}
          index={tabIndex}
          size="sm"
          isLazy
          variant="soft-rounded">
          <TabPanels minH="0" flex={1}>
            <TabPanel p={3} h="100%">
              <GynIntakeForm user={user} />
            </TabPanel>
            <TabPanel h="100%" p={0} overflow="hidden">
              <GynProfileEncounters />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Flex>
    </GynCareViewProvider>
  )
}

const GynProfileViewBody = ({
  user,
  width,
  height,
}: {
  user: WithId<User> | null
  width: number
  height: number
}) => (
  <Resizable
    width={width}
    height={height}
    localStorageId="gyn-profile-resize"
    leftChild={() => <GynProfileViewContent user={user} width={width} height={height} />}
    rightChild={() => <GynLog maxHeight={height} />}
  />
)

export const GynProfileView = () => {
  const { user } = useProfile()
  const bodyRef = useRef<HTMLDivElement>(null)

  const { width, height } = useResizeObserver(bodyRef, 'content')
  return (
    <Box ref={bodyRef} w="100%" h="100%" overflow="hidden">
      {height && width ? <GynProfileViewBody user={user} width={width} height={height} /> : null}
    </Box>
  )
}
